import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Stack,
  Typography,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import TableCompo from "../CommonTable/TableCompo";
import { getJewelleryOrdersRequest } from "../../redux/Jewellery/jewellerySlice";
import { useAppTheme } from "../../theme/useAppTheme";
import useCurrency from "../../hooks/useCurrency";

const FILTERS = [
  "All",
  "PENDING",
  "SUCCESS",
  "CANCELLED",
  "READYTODISPATCH",
  "DISPATCHED",
  "RETURNED",
];

const JewelleryOrder = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const themeColors = useAppTheme();
  const { symbol } = useCurrency();
  const { ordersLoading, orders } = useSelector((state) => state.jewellery || {});
  const [filter, setFilter] = useState("All");
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(10);

  useEffect(() => {
    dispatch(getJewelleryOrdersRequest({ page: 1, limit: 100 }));
  }, [dispatch]);

  const orderList = Array.isArray(orders?.data) ? orders.data : [];
  const filtered = orderList.filter((item) => {
    if (filter === "All") return true;
    return String(item.status || "").toUpperCase() === filter;
  });
  const paginated = filtered.slice(page * limit, page * limit + limit);

  const columns = [
    {
      field: "name",
      headerName: "Artificial Jewellery",
      flex: 1.2,
      render: (value, row) => row.name || "Artificial Jewellery",
    },
    {
      field: "productCode",
      headerName: "Code",
      flex: 0.8,
      render: (value, row) => row.productCode || "-",
    },
    {
      field: "quantity",
      headerName: "Qty",
      flex: 0.5,
      render: (value, row) => row.quantity || 1,
    },
    {
      field: "amount",
      headerName: `Amount (${symbol})`,
      flex: 0.8,
      render: (value, row) =>
        Number(row.amount || 0).toLocaleString("en-IN", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }),
    },
    {
      field: "status",
      headerName: "Status",
      flex: 0.9,
      render: (value, row) => row.status || "PENDING",
    },
    {
      field: "action",
      headerName: "Action",
      flex: 0.9,
      sortable: false,
      render: (value, row) => (
        <Button
          variant="contained"
          size="small"
          sx={{
            textTransform: "none",
            borderRadius: "20px",
            background: themeColors.mediumContainer,
            color: themeColors.text,
          }}
          onClick={() =>
            navigate("/artificialJewelleryOrderDetails", { state: { order: row } })
          }
        >
          View Details
        </Button>
      ),
    },
  ];

  return (
    <Box sx={{ width: "100%", display: "flex", justifyContent: "center", mt: 4 }}>
      <Card
        sx={{
          width: "98%",
          maxWidth: "1300px",
          boxShadow: "0 4px 10px rgba(0,0,0,0.1)",
          borderRadius: 3,
          background: themeColors.scaffoldGradientColors,
          border: `1px solid ${themeColors.scaffold}`,
          color: themeColors.text,
        }}
      >
        <CardContent>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography
              variant="h5"
              sx={{ mb: 2, fontWeight: 700, color: themeColors.primary }}
            >
              Artificial Jewellery Orders
            </Typography>
            <Button
              onClick={() => navigate("/artificialJewellery")}
              sx={{ textTransform: "none", color: themeColors.primary }}
            >
              Continue shopping
            </Button>
          </Stack>

          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mb: 3 }}>
            {FILTERS.map((status) => (
              <Button
                key={status}
                size="small"
                onClick={() => {
                  setFilter(status);
                  setPage(0);
                }}
                sx={{
                  textTransform: "none",
                  borderRadius: "20px",
                  px: 2,
                  fontWeight: 600,
                  color: themeColors.text,
                  background: filter === status ? themeColors.primary : "transparent",
                  border: `1px solid ${themeColors.scaffold}`,
                }}
              >
                {status}
              </Button>
            ))}
          </Stack>

          {ordersLoading ? (
            <Box sx={{ textAlign: "center", mt: 8 }}>
              <CircularProgress />
              <Typography variant="body2" mt={2}>
                Loading artificial jewellery orders...
              </Typography>
            </Box>
          ) : filtered.length === 0 ? (
            <Typography textAlign="center" mt={5}>
              No artificial jewellery orders found
            </Typography>
          ) : (
            <Box sx={{ height: "70vh", width: "100%", minWidth: 0, overflow: "auto" }}>
              <TableCompo
                title="Artificial Jewellery Orders"
                columns={columns}
                data={paginated}
                page={page}
                limit={limit}
                totalCount={filtered.length}
                loading={ordersLoading}
                onTableChange={(type, payload) => {
                  if (type === "changePage") setPage(payload.page);
                  if (type === "changeRowsPerPage") {
                    setLimit(payload.rowsPerPage);
                    setPage(0);
                  }
                }}
              />
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default JewelleryOrder;
